import type { LayoutFeatures } from '@mimir/web/model/layoutFeatures';
import { prepareSearchRouterQueryParams } from '@mimir/web/pages/search/util';
import { getGlobalRouter } from '@mimir/web/router';
import { getGlobalStore } from '@mimir/web/store';
import type { FolderDto, FolderPathSegmentDto, FolderViewModel } from '@mimir/common/dto/folderModels';
import type { Filters } from '@mimir/common/filters/Filters';
import { getFolder } from '@mimir/web/api/globalClientFolder';
import { getFolderFromCache } from '../globals/folders';
import { RootFolder, checkIfRoot } from '@mimir/web/util/path-to-root';


export const getFolderFromCacheOrServer = async (folderId: string): Promise<FolderDto | FolderViewModel> => {
  let folder = getFolderFromCache(folderId) as FolderViewModel | undefined | FolderDto;
  if (!folder) {
    folder = await getFolder(folderId);
  }
  return folder;
};

export const getFormattedFolderName = (folder: FolderViewModel | null, maxCharacters = 25) => {
  // Check if the folder is the root folder
  if (folder && checkIfRoot(folder)) {
    return RootFolder.DisplayName;
  } else if (!folder) {
    return '(None selected)';
  }
  const folderName = folder?.name ?? RootFolder.DisplayName;
  return `${folderName.length > maxCharacters ? `${folderName.slice(0, maxCharacters)}...` : folderName}`;
};

export interface GoToFolderParams {
  id: string;
  features?: LayoutFeatures;
  clearSearchString?: boolean;
}
/**
 * Single source of truth function to navigate to a folder. Any place in the application that wants to navigate should use
 * this function to do it, to ensure relevant pieces of state are kept in sync properly.
 */

export const goToFolder = (params: GoToFolderParams) => {
  const { id, features, clearSearchString = true } = params;
  // If we detect the user has searched for folder names and double clicks into a folder, we explicitly clear it
  // as that is what the user will most likely expect to happen.
  const filters = getGlobalStore().getters.filters as Filters;
  const folderFilter = filters.defaultFilters['filter:folder'];
  if (folderFilter?.hasValue) {
    folderFilter.clearValue();
  }

  filters.defaultFilters['filter:folderId'].setValue([id]);
  const queryParams = prepareSearchRouterQueryParams();

  if (clearSearchString) {
    queryParams.searchString = '';
  }

  let defaultRoute = 'folderView';
  if (features?.browse)
    defaultRoute = 'export';
  else if (features?.export)
    defaultRoute = 'browse';
  else if (features?.cutter)
    defaultRoute = 'sourceBrowser';


  const router = getGlobalRouter();

  router.push({
    name: resolveNavigationRoute(router.currentRoute.name, defaultRoute),
    query: queryParams,
  });
};

const resolveNavigationRoute = (currentRoute: string | null | undefined, defaultRoute: string): string => {
  if (!currentRoute) {
    return defaultRoute;
  }
  // TODO: route names should be exported constants somewhere
  const routesThatStayOnCurrentRoute = ['browse', 'export'];
  // Cutter related routes that should go to sourceBrowser when navigating to a folder
  const cutterRoutes = ['sourceBrowser', 'sequenceProperties', 'boxProperties', 'controlPointEditor', 'itemProperties'];

  if (routesThatStayOnCurrentRoute.includes(currentRoute)) {
    return currentRoute;
  }
  if (cutterRoutes.includes(currentRoute)) {
    return 'sourceBrowser';
  }
  console.log('Defaulting to route:', defaultRoute, 'from current route:', currentRoute);

  return defaultRoute;
};

export const stringifiedPath = (paths: FolderPathSegmentDto[], excludeRootFolder = false) => {
  const pathNames = paths.map(path => path.name);
  if (excludeRootFolder) pathNames.shift();
  return pathNames.join('/');
};
